import { SignatureFrame } from "./SignatureFrame";

interface Lane {
  y: number;
  label: string;
  spans: Array<[from: number, to: number]>;
  gold?: boolean;
}

const SEMS = [1, 2, 3, 4, 5, 6, 7, 8];

const LANES: Lane[] = [
  { y: 80, label: "FOUNDATION", spans: [[1, 4]] },
  { y: 130, label: "CORE", spans: [[3, 6]] },
  { y: 180, label: "SPECIALIZE", spans: [[5, 8]], gold: true },
  { y: 230, label: "PROJECT WORK", spans: [[7, 8]] },
  { y: 280, label: "INTERNSHIPS", spans: [[6, 8]] },
];

const MILESTONES = [
  // Branch entry happens at the start of the second year, so it belongs on
  // semester 3 — not 4.
  { s: 3, label: "BRANCH ENTRY" },
  { s: 6, label: "CORE COMPLETE" },
  { s: 8, label: "GRADUATION" },
];

/**
 * A year spans two semesters, so its label sits at the midpoint of that pair:
 * halfway between semester 1 and 2 is s = 1.5, and so on.
 *
 * These were 2.5 / 4.5 / 6.5 / 8.5 and were drawn at `semX(s)` rather than the
 * `semX(s) + 35` every other element uses to reach a column's centre. The two
 * errors compounded: YEAR 1 landed past semester 2's centre, so each year read
 * as covering the wrong pair of semesters and YEAR 4 sat off the last column
 * entirely.
 */
const YEAR_MARKERS = [
  { s: 1.5, label: "YEAR 1" },
  { s: 3.5, label: "YEAR 2" },
  { s: 5.5, label: "YEAR 3" },
  { s: 7.5, label: "YEAR 4" },
];

const semX = (s: number) => 110 + (s - 1) * 95;

export function CurriculumMap() {
  return (
    <SignatureFrame
      title="Four years, eight semesters — how a B.E. unfolds"
      code="MAP-CUR-003"
      meta="Reference flow"
    >
      <svg viewBox="0 0 920 360" preserveAspectRatio="xMidYMid meet">
        {SEMS.map((s) => (
          <g key={s} className="illo-pop">
            <text x={semX(s) + 35} y={48} textAnchor="middle" className="illo-label-bold">
              SEM {s.toString().padStart(2, "0")}
            </text>
            <line x1={semX(s)} y1={60} x2={semX(s) + 70} y2={60} className="illo-stroke" />
          </g>
        ))}

        {SEMS.map((s) => (
          <line
            key={s}
            x1={semX(s) + 35}
            y1={70}
            x2={semX(s) + 35}
            y2={310}
            className="illo-stroke"
            strokeWidth="0.5"
            opacity="0.3"
            strokeDasharray="2 4"
          />
        ))}

        {LANES.map((lane, idx) => (
          <g key={idx} className={`illo-pop illo-pop-d${Math.min(idx + 1, 5)}`}>
            <text x="14" y={lane.y + 14} className="illo-label-bold" textAnchor="start">
              {lane.label}
            </text>
            {lane.spans.map(([from, to], i) => {
              const x1 = semX(from);
              const x2 = semX(to) + 70;
              return (
                <g key={i}>
                  <rect
                    x={x1}
                    y={lane.y}
                    width={x2 - x1}
                    height={28}
                    rx="2"
                    fill={lane.gold ? "var(--gold-500)" : "var(--paper)"}
                    stroke={lane.gold ? "var(--gold-700)" : "var(--navy-800)"}
                    strokeWidth={lane.gold ? "1" : "1.5"}
                  />
                  {Array.from({ length: to - from }, (_, k) => (
                    <line
                      key={k}
                      x1={semX(from + k + 1)}
                      y1={lane.y + 4}
                      x2={semX(from + k + 1)}
                      y2={lane.y + 24}
                      stroke={lane.gold ? "var(--navy-900)" : "var(--ink-300)"}
                      strokeWidth="0.7"
                    />
                  ))}
                </g>
              );
            })}
          </g>
        ))}

        <g className="illo-pop illo-pop-d5">
          {MILESTONES.map((m, i) => (
            <g key={i}>
              <circle cx={semX(m.s) + 35} cy="325" r="5" className="illo-gold-fill" />
              <line
                x1={semX(m.s) + 35}
                y1="305"
                x2={semX(m.s) + 35}
                y2="320"
                className="illo-gold-stroke"
              />
              <text
                x={semX(m.s) + 35}
                y="350"
                textAnchor="middle"
                className="illo-label-bold"
                fill="var(--gold-700)"
              >
                {m.label}
              </text>
            </g>
          ))}
        </g>

        {YEAR_MARKERS.map((y, i) => (
          <text
            key={i}
            x={semX(y.s) + 35}
            y={28}
            textAnchor="middle"
            className="illo-numeral"
            style={{ fontSize: "14px" }}
          >
            {y.label}
          </text>
        ))}
      </svg>
    </SignatureFrame>
  );
}
